// @ts-check
import { iterateEach } from '@agoric/casting';
import { AmountMath, AssetKind } from '@agoric/ertp';
import { objectMap } from '@agoric/internal';
import {
  makeAsyncIterableFromNotifier,
  makeNotifierKit,
} from '@agoric/notifier';
import {
  assertHasData,
  NO_SMART_WALLET_ERROR,
} from '@agoric/smart-wallet/src/utils';
import { E, ERef, FarRef } from '@endo/eventual-send';
import { Far, Marshal } from '@endo/marshal';
import { querySwingsetParams } from '../util/querySwingsetParams';
import { getDappService } from '../service/Dapps';
import { getIssuerService } from '../service/Issuers';
import { getOfferService } from '../service/Offers';
import { isCopyBag } from '@endo/patterns';

import type {
  Brand,
  DepositFacet,
  DisplayInfo,
  Issuer,
} from '@agoric/ertp/src/types';
import type { Notifier } from '@agoric/notifier/src/types';
import type { OfferStatus } from '@agoric/smart-wallet/src/offers';
import type { UpdateRecord } from '@agoric/smart-wallet/src/smartWallet';
import type { Petname } from '@agoric/smart-wallet/src/types';
import type { SmartWalletKey } from '../store/Dapps';
import type { ValueFollower } from '@agoric/casting/src/follower-cosmjs';
import type { CurrentWalletRecord } from '@agoric/smart-wallet/src/smartWallet';
import { KeplrUtils } from '../contexts/Provider.jsx';
import { HttpEndpoint } from '@cosmjs/tendermint-rpc';
import type { ValueFollowerElement } from '@agoric/casting/src/types';
import { queryBankBalances } from './queryBankBalances';
import type { Coin } from '@cosmjs/stargate';
import type { PurseInfo } from '../service/Offers';
import { wellKnownPetnames } from './well-known-petnames';
import type { ChainStorageWatcher } from '@agoric/rpc';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AgoricChainStoragePathKind } from '@agoric/rpc';

const newId = kind => `${kind}${Math.random()}`;
const POLL_INTERVAL_MS = 6000;

export type BackendSchema = {
  actions: FarRef<{
    createPurse: (issuer: any, id?: string) => void;
    createContact: (depositFacet: DepositFacet, id?: string) => void;
    createIssuer: (issuer: Issuer, id?: string) => void;
  }>;
  beansOwing: Promise<AsyncIterator<number>>;
  services: Promise<AsyncIterator<any, any>>;
  contacts: Promise<AsyncIterator<any, any>>;
  dapps: Promise<AsyncIterator<any, any>>;
  issuers: Promise<AsyncIterator<any, any>>;
  offers: ReturnType<typeof wrapOffersIterator>;
  payments: Promise<AsyncIterator<any, any>>;
  purses: Promise<AsyncIterator<any, any>>;
  issuerSuggestions: Promise<AsyncIterator<any, any>>;
  swingsetParams: Promise<any>;
};

const wrapOffersIterator = (
  offersMembers: AsyncIterator<any[], any[]>,
  walletBridge: any,
) =>
  harden({
    next: async () => {
      const { done, value } = await E(offersMembers).next();
      return harden({
        done,
        value:
          value &&
          value.map(({ id, ...rest }) =>
            harden({
              id,
              ...rest,
              actions: {
                // Provide these synthetic actions since offers don't have any yet.
                accept: () => E(walletBridge).acceptOffer(id),
                decline: () => E(walletBridge).declineOffer(id),
                tryExit: () => E(walletBridge).tryExitOffer(id),
              },
            }),
          ),
      });
    },
    return: offersMembers.return,
    throw: offersMembers.throw,
  });

export const makeBackendFromWalletBridge = (
  walletBridge: ReturnType<typeof makeWalletBridgeFromFollowers>,
) => {
  const iterateNotifier = async <T>(notifier: ERef<Notifier<T>>) =>
    makeAsyncIterableFromNotifier(notifier)[Symbol.asyncIterator]();

  // XXX we don't have access to the board yet.
  const { notifier: servicesNotifier } = makeNotifierKit();

  const firstSchema: BackendSchema = harden({
    actions: Far('schemaActions', {
      createPurse: (issuer, id = newId('Purse')) =>
        E(walletBridge).makeEmptyPurse(issuer?.issuerPetname, id),
      createContact: (depositFacet, id = newId('Contact')) =>
        E(walletBridge).addContact(id, depositFacet),
      createIssuer: (issuer, id = newId('Issuer')) =>
        E(walletBridge).addIssuer(id, issuer, true),
    }),
    beansOwing: iterateNotifier(E(walletBridge).getBeansOwingNotifier()),
    services: iterateNotifier(servicesNotifier),
    contacts: iterateNotifier(E(walletBridge).getContactsNotifier()),
    dapps: iterateNotifier(E(walletBridge).getDappsNotifier()),
    issuers: iterateNotifier(E(walletBridge).getIssuersNotifier()),
    offers: wrapOffersIterator(
      // @ts-expect-error xxx
      iterateNotifier(E(walletBridge).getOffersNotifier()),
      walletBridge,
    ),
    payments: iterateNotifier(E(walletBridge).getPaymentsNotifier()),
    purses: iterateNotifier(E(walletBridge).getPursesNotifier()),
    issuerSuggestions: iterateNotifier(
      E(walletBridge).getIssuerSuggestionsNotifier(),
    ),
    swingsetParams: E(walletBridge).getSwingsetParams(),
  });

  // Just produce a single update for the initial backend.
  // TODO: allow further updates.
  /** FIXME type {NotifierRecord<BackendSchema>} */
  const { notifier: backendNotifier, updater: backendUpdater } =
    makeNotifierKit(firstSchema);

  const backendIt = iterateNotifier(backendNotifier);

  const cancel = e => {
    backendUpdater.fail(e);
    walletBridge.cleanup();
  };

  return { backendIt, cancel };
};

type VbankInfo = {
  brand: Brand;
  displayInfo: DisplayInfo<'nat'>;
  issuerName: string;
};

type VbankUpdate = [string, VbankInfo][];

type AgoricBrandsUpdate = [string, Brand][];

export const makeWalletBridgeFromFollowers = (
  smartWalletKey: SmartWalletKey,
  rpc: HttpEndpoint,
  marshaller: Marshal<string>,
  currentFollower: Promise<ValueFollower<Partial<CurrentWalletRecord>>>,
  updateFollower: Promise<ValueFollower<Partial<UpdateRecord>>>,
  watcher: ChainStorageWatcher,
  keplrConnection: KeplrUtils,
  errorHandler: (e: any) => void | ((e: any) => never) = e => {
    // Make an unhandled rejection.
    throw e;
  },
  firstCallback: () => void | undefined = () => {},
) => {
  let isHalted = false;
  let isBankLoaded = false;
  let isSmartWalletLoaded = false;
  let isOfferServiceStarted = false;
  const stopWatchingHooks = Array<() => void>();

  const notifiers = {
    getPursesNotifier: 'purses',
    getContactsNotifier: 'contacts',
    getIssuersNotifier: 'issuers',
    getOfferUpdatesNotifier: 'offerUpdates',
    getPendingOffersNotifier: 'pendingOffers',
    getPaymentsNotifier: 'payments',
  };

  const notifierKits = Object.fromEntries(
    Object.entries(notifiers).map(([_method, stateName]) => [
      stateName,
      makeNotifierKit(null as any),
    ]),
  );

  // We assume just one cosmos purse per brand.
  const brandToPurse = new Map<Brand, PurseInfo>();
  const pursePetnameToBrand = new Map<Petname, Brand>();

  const updatePurses = () => {
    console.debug('brandToPurse map', brandToPurse);
    const purses = [] as PurseInfo[];
    for (const [brand, purse] of brandToPurse.entries()) {
      if (purse.currentAmount && purse.brandPetname) {
        assert(purse.pursePetname, 'missing purse.pursePetname');
        pursePetnameToBrand.set(purse.pursePetname, brand);
        purses.push(purse);
      }
    }
    notifierKits.purses.updater.updateState(harden(purses));

    // Make sure the offer service has all purses from both bank and smart
    // wallet chainstorage.
    if (!isOfferServiceStarted && isBankLoaded && isSmartWalletLoaded) {
      isOfferServiceStarted = true;
      offerService.start(pursePetnameToBrand, brandToPurse);
    }
  };

  const signSpendAction = async (data: string) => {
    const {
      signers: { interactiveSigner },
    } = keplrConnection;
    if (!interactiveSigner) {
      throw new Error(
        'Cannot sign a transaction in read only mode, connect to keplr.',
      );
    }
    return interactiveSigner.submitSpendAction(data);
  };

  const getNotifierMethods = objectMap(
    notifiers,
    stateName => () => notifierKits[stateName].notifier,
  );

  const offerUpdatesNotifer: Notifier<OfferStatus> =
    getNotifierMethods.getOfferUpdatesNotifier();

  const pendingOffersNotifier: Notifier<OfferStatus> =
    getNotifierMethods.getPendingOffersNotifier();

  const offerService = getOfferService(
    smartWalletKey,
    signSpendAction,
    offerUpdatesNotifer,
    pendingOffersNotifier,
    marshaller,
    watcher,
  );

  const { notifier: beansOwingNotifier, updater: beansOwingUpdater } =
    makeNotifierKit<Number | null>(null);

  const watchBeansOwing = () => {
    stopWatchingHooks.push(
      watcher.watchLatest(
        [
          AgoricChainStoragePathKind.Data,
          `beansOwing.${smartWalletKey.address}`,
        ],
        value => {
          beansOwingUpdater.updateState(Number(value));
        },
        err => {
          console.error('error watching beansOwing', err);
          // Don't throw on beansOwing, for upgrade backwards compatibility.
          // errorHandler(new Error(err));
        },
      ),
    );
  };

  // Infers purse balances from cosmos bank module balances since purses are
  // lazily instantiated in the smart wallet.
  const watchChainBalances = () => {
    let vbankAssets: VbankUpdate;
    let bank: Coin[];

    const possiblyUpdateBankPurses = () => {
      if (!vbankAssets || !bank) return;

      const bankMap = new Map<string, string>(
        bank.map(({ denom, amount }) => [denom, amount]),
      );

      vbankAssets.forEach(([denom, info]) => {
        // Show the vbank asset as a purse with 0 balance if the user doesn't
        // have any. This way it will show up on their asset list with the
        // deposit action available.
        const amount = bankMap.get(denom) ?? 0n;
        const petname = wellKnownPetnames[info.issuerName] ?? info.issuerName;

        const purseInfo: PurseInfo = {
          brand: info.brand,
          currentAmount: AmountMath.make(info.brand, BigInt(amount)),
          brandPetname: petname,
          pursePetname: petname,
          displayInfo: info.displayInfo,
          denom,
        };
        brandToPurse.set(info.brand, purseInfo);
      });

      isBankLoaded = true;
      updatePurses();
    };

    const watchBank = async () => {
      if (isHalted) return;
      try {
        bank = await queryBankBalances(keplrConnection.address, rpc);
      } catch (e) {
        errorHandler(e);
        return;
      }
      possiblyUpdateBankPurses();
      setTimeout(watchBank, POLL_INTERVAL_MS);
    };

    const watchVbankAssets = () => {
      stopWatchingHooks.push(
        watcher.watchLatest<VbankUpdate>(
          [AgoricChainStoragePathKind.Data, 'published.agoricNames.vbankAsset'],
          value => {
            vbankAssets = value;
            possiblyUpdateBankPurses();
          },
          err => {
            console.error('error watching vbank assets', err);
            errorHandler(new Error(err));
          },
        ),
      );
    };

    void watchVbankAssets();
    void watchBank();
  };

  const fetchAgoricBrands = () =>
    new Promise<Map<Brand, string>>((res, rej) => {
      const cancel = watcher.watchLatest(
        [AgoricChainStoragePathKind.Data, 'published.agoricNames.brand'],
        value => {
          cancel();
          res(new Map((value as AgoricBrandsUpdate).map(([k, v]) => [v, k])));
        },
        err => {
          cancel();
          rej(err);
        },
      );
    });

  const watchPendingOffers = () => {
    stopWatchingHooks.push(
      watcher.watchLatest<Partial<CurrentWalletRecord>>(
        [
          AgoricChainStoragePathKind.Data,
          `published.wallet.${smartWalletKey.address}.current`,
        ],
        value => {
          notifierKits.pendingOffers.updater.updateState(
            value.liveOffers ?? [],
          );
        },
        err => {
          console.error('error watching pending offers', err);
          errorHandler(new Error(err));
        },
      ),
    );
  };

  const fetchCurrent = async () => {
    const resolvedFollower = await currentFollower;
    await assertHasData(resolvedFollower);
    watchBeansOwing();
    watchPendingOffers();
    watchChainBalances();

    const latestIterable = await E(resolvedFollower).getLatestIterable();
    const iterator = latestIterable[Symbol.asyncIterator]();
    const latest = await iterator.next();
    if (firstCallback) {
      firstCallback();
      Object.values(notifierKits).forEach(({ updater }) =>
        updater.updateState([]),
      );
      // @ts-expect-error xxx param mutation
      firstCallback = undefined;
    }
    const currentEl: ValueFollowerElement<Partial<CurrentWalletRecord>> =
      latest.value;
    const wallet = currentEl.value;
    console.debug('wallet current', wallet);

    const agoricBrands = await fetchAgoricBrands();
    assert(agoricBrands, 'Failed to fetch agoric brands');

    for (const purse of wallet.purses ?? []) {
      // Non 'set' amounts need to be fetched from vbank to know their
      // decimalPlaces, so we can skip them. Currently this means all assets
      // except zoe invites are read via `watchChainBalances`.
      //
      // If we ever add non 'set' amount purses that aren't in the vbank, it's
      // not currently possible to read their decimalPlaces, so this code
      // will need updating.

      const isCopyBagResult = isCopyBag(purse.balance.value);

      if (!Array.isArray(purse.balance.value) && !isCopyBagResult) {
        console.debug('skipping non-set amount', purse.balance.value);
        continue;
      }
      if (!agoricBrands.has(purse.brand)) {
        console.warn('skipping unknown brand', purse.brand);
        continue;
      }
      const brandDescriptor = {
        petname: agoricBrands.get(purse.brand) as Petname,
        displayInfo: isCopyBagResult
          ? { assetKind: AssetKind.COPY_BAG }
          : { assetKind: AssetKind.SET },
      };
      const purseInfo: PurseInfo = {
        brand: purse.brand,
        currentAmount: purse.balance,
        brandPetname: brandDescriptor.petname,
        pursePetname: brandDescriptor.petname,
        displayInfo: brandDescriptor.displayInfo,
      };
      brandToPurse.set(purse.brand, purseInfo);
    }
    isSmartWalletLoaded = true;
    updatePurses();
    return currentEl.blockHeight;
  };

  const followLatest = async startingHeight => {
    for await (const { value } of iterateEach<{ value: UpdateRecord }>(
      updateFollower,
      {
        height: startingHeight,
      },
    )) {
      const updateRecord = value as UpdateRecord;

      if (updateRecord.updated === undefined) {
        console.warn('Empty update, skipping');
        continue;
      }

      switch (updateRecord.updated) {
        case 'balance': {
          // TODO: Don't assume just one purse per brand.
          // https://github.com/Agoric/agoric-sdk/issues/6126
          const { currentAmount } = updateRecord;
          const purseObj = {
            ...brandToPurse.get(currentAmount.brand),
            currentAmount,
            value: currentAmount.value,
          };
          brandToPurse.set(currentAmount.brand, purseObj);
          updatePurses();
          break;
        }
        case 'offerStatus': {
          const { status } = updateRecord;
          notifierKits.offerUpdates.updater.updateState(status);
          break;
        }
        default: {
          throw Error(`Unknown updateRecord ${updateRecord}`);
        }
      }
    }
  };

  const loadData = () =>
    fetchCurrent().then(height => followLatest(height).catch(errorHandler));

  const retry = () => {
    loadData().catch(e => {
      if (e.message === NO_SMART_WALLET_ERROR) {
        setTimeout(retry, 5000);
      } else {
        errorHandler(e);
      }
    });
  };

  loadData().catch(e => {
    errorHandler(e);
    if (e.message === NO_SMART_WALLET_ERROR) {
      setTimeout(retry, 5000);
    }
  });

  const makeEmptyPurse = (_issuerPetname, _purseId) => {
    console.log('make empty purse');
  };

  const addContact = (_boardId, _petname) => {
    console.log('add contact');
  };

  const addIssuer = (_boardId, _petname, _makeDefaultPurse) => {
    console.log('add issuer');
  };

  const issuerService = getIssuerService(signSpendAction);
  const dappService = getDappService(smartWalletKey);
  const { acceptOffer, declineOffer, tryExitOffer } = offerService;

  const {
    getContactsNotifier,
    getIssuersNotifier,
    getPaymentsNotifier,
    getPursesNotifier,
  } = getNotifierMethods;

  const fetchSwingsetParams = () =>
    querySwingsetParams(rpc).then(res => res.params);

  const walletBridge = Far('follower wallet bridge', {
    getDappsNotifier: () => dappService.notifier,
    getOffersNotifier: () => offerService.notifier,
    getIssuerSuggestionsNotifier: () => issuerService.notifier,
    getSwingsetParams: () => fetchSwingsetParams(),
    getBeansOwingNotifier: () => beansOwingNotifier,
    getIssuersNotifier,
    getContactsNotifier,
    getPaymentsNotifier,
    getPursesNotifier,
    acceptOffer,
    declineOffer,
    tryExitOffer,
    makeEmptyPurse,
    addContact,
    addIssuer,
    cleanup: () => {
      while (stopWatchingHooks.length) {
        // @ts-expect-error always defined because length > 0
        stopWatchingHooks.pop()();
      }
    },
  });

  return walletBridge;
};
